import { VisuallyHidden } from '@artosera/ui';
import { Lock } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import type { GalerieSession, SessionUtilisateur } from '../../api/types.js';
import { useConsultationHorsLigne } from '../../horsLigne/index.js';
import { useShellContext } from '../shell/context.js';
import styles from './lectureSeule.module.css';

/**
 * Réglages en lecture seule, pour deux raisons :
 *
 * - `hors_ligne` : la consultation hors ligne (M11). L'appareil relit un
 *   instantané chiffré ; le client HTTP refuse toute requête
 *   (`api/client.ts`) et les droits affichés ne sont plus revérifiés. Le
 *   serveur n'en sait rien — c'est une session ordinaire au retour du
 *   réseau —, la fermeture se joue donc ici et dans le client ;
 * - `role` : le rôle Lecture seule. Le serveur refuse déjà chaque écriture
 *   (matrice 5.4) ; ici, les écrans de la galerie se ferment au lieu de
 *   s'ouvrir sans rien de modifiable.
 *
 * Trois sorts : `accessible` (rien ne part au serveur), `grise` (la carte
 * reste visible, inerte), `masque` (ce qui ouvre un accès à un tiers, engage
 * de l'argent ou fait sortir des données). Un écran absent de la table est
 * masqué : un réglage nouveau reste fermé tant qu'il n'est pas classé.
 */
export type SortEnLectureSeule = 'accessible' | 'grise' | 'masque';

/** Par chemin sous `reglages/`, ou par renvoi hors de Réglages. */
export const REGLAGES_EN_LECTURE_SEULE: Readonly<Record<string, SortEnLectureSeule>> = {
  // L'instantané de cet appareil : « Effacer » est local et fait sortir de la consultation.
  'hors-ligne': 'accessible',

  modules: 'grise',
  securite: 'grise',
  lieux: 'grise',
  emplacements: 'grise',
  diffusion: 'grise',
  negociation: 'grise',
  profils: 'grise',
  assurance: 'grise',
  documents: 'grise',
  pipeline: 'grise',
  'rendez-vous-en-ligne': 'grise',
  'espace-collectionneur': 'grise',
  'boite-reception': 'grise',
  'assistant-reponse': 'grise',
  'assistant-recherche': 'grise',

  // Accès ouverts à des personnes ou à des services tiers.
  equipe: 'masque',
  'acces-venio': 'masque',
  'acces-api': 'masque', // porte aussi l'export FEC
  'assistant-mcp': 'masque',
  connecteurs: 'masque',
  ia: 'masque',
  // Argent : add-ons payants, trésorerie, facturation.
  addons: 'masque',
  'facturation-artosera': 'masque',
  tresorerie: 'masque',
  'ventes?onglet=reglages': 'masque',
  'archive-factures': 'masque',
  'archive-factures#anterieures': 'masque',
  'archive-registre-police': 'masque',
};

export type RaisonLectureSeule = 'hors_ligne' | 'role';

/** Le sort d'un renvoi de Réglages (`reglages/lieux`, `archive-factures`…). */
export function sortEnLectureSeule(vers: string, raison: RaisonLectureSeule): SortEnLectureSeule {
  const cle = vers.startsWith('reglages/') ? vers.slice('reglages/'.length) : vers;
  // Le compte lui-même (double authentification), pas la galerie : réglable
  // en ligne quel que soit le rôle.
  if (raison === 'role' && cle === 'securite') return 'accessible';
  return REGLAGES_EN_LECTURE_SEULE[cle] ?? 'masque';
}

/**
 * Pourquoi Réglages est en lecture seule, ou `null`. La consultation hors
 * ligne l'emporte : sa session porte aussi le rôle Lecture.
 */
export function useLectureSeule(
  session: SessionUtilisateur,
  galerie: GalerieSession,
): RaisonLectureSeule | null {
  const consultation = useConsultationHorsLigne();
  if (session.consultationHorsLigne === true || consultation !== null) return 'hors_ligne';
  return galerie.role === 'lecture' ? 'role' : null;
}

/** Le bandeau unique : pourquoi, et comment en sortir. */
export function BandeauLectureSeule({
  raison,
}: {
  readonly raison: RaisonLectureSeule;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const cle = raison === 'role' ? 'reglages.lectureSeule.role' : 'reglages.lectureSeule.horsLigne';
  return (
    <div className={styles.bandeau} role="note" data-ui="settings-read-only">
      <span className={styles.icone} aria-hidden="true">
        <Lock size={16} strokeWidth={2} />
      </span>
      <div className={styles.texte}>
        <p className={styles.titre}>{t('reglages.lectureSeule.titre')}</p>
        <p>{t(`${cle}.pourquoi`)}</p>
        <p>{t(`${cle}.sortir`)}</p>
      </div>
    </div>
  );
}

/**
 * Une carte du sommaire : lien, carte inerte ou rien, selon le sort de son
 * écran en lecture seule.
 */
export function CarteReglage({
  vers,
  galerieSlug,
  lectureSeule,
  className,
  classeGrisee,
  children,
}: {
  readonly vers: string;
  readonly galerieSlug: string;
  readonly lectureSeule: RaisonLectureSeule | null;
  readonly className: string | undefined;
  readonly classeGrisee: string | undefined;
  readonly children: ReactNode;
}): JSX.Element | null {
  const { t } = useTranslation('regie');
  const sort = lectureSeule ? sortEnLectureSeule(vers, lectureSeule) : 'accessible';
  if (sort === 'masque') return null;
  if (sort === 'grise') {
    return (
      <li>
        <span
          className={[className, classeGrisee].filter(Boolean).join(' ')}
          aria-disabled="true"
          title={t('reglages.lectureSeule.indisponible')}
        >
          {children}
          <VisuallyHidden>{t('reglages.lectureSeule.indisponible')}</VisuallyHidden>
        </span>
      </li>
    );
  }
  return (
    <li>
      <Link to={`/${galerieSlug}/${vers}`} className={className}>
        {children}
      </Link>
    </li>
  );
}

/**
 * Garde des écrans de Réglages : une adresse saisie à la main n'ouvre pas en
 * lecture seule ce que le sommaire ferme. Le contexte de la coquille est
 * transmis tel quel aux écrans.
 */
export function GardeReglagesLectureSeule(): JSX.Element {
  const contexte = useShellContext();
  const { t } = useTranslation('regie');
  const lectureSeule = useLectureSeule(contexte.session, contexte.galerie);
  const { pathname } = useLocation();
  // Montée sur `:slug/reglages` : l'écran est le troisième segment, quelle
  // que soit la casse acceptée par le routeur. Un segment inconnu est masqué.
  const ecran = (pathname.split('/').filter(Boolean)[2] ?? '').toLowerCase();
  if (
    lectureSeule &&
    ecran &&
    sortEnLectureSeule(`reglages/${ecran}`, lectureSeule) !== 'accessible'
  ) {
    return (
      <section className={styles.page}>
        <h1 className={styles.titrePage}>{t('reglages.titre')}</h1>
        <BandeauLectureSeule raison={lectureSeule} />
        <p>
          {t(`reglages.lectureSeule.${lectureSeule === 'role' ? 'role' : 'horsLigne'}.ecranFerme`)}
        </p>
        <Link to={`/${contexte.galerieSlug}/reglages`}>{t('reglages.lectureSeule.retour')}</Link>
      </section>
    );
  }
  return <Outlet context={contexte} />;
}
